import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { DashboardOverview, MetricPair } from '@/types'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import { formatTrendPercent, metricPairOrEmpty, trendDirection } from './dashboardFormat'
import { cn } from '@/lib/utils'
import { MetricLabel } from '@/components/finance/MetricLabel'
import { DataCompletenessBadge } from '@/components/finance/DataCompletenessBadge'
import type { MetricKey } from '@/lib/financeGlossary'
import { cogsBasisDisplay } from '@/components/finance/cogsBasisDisplay'

interface RevenueProfitPanelProps {
  overview?: DashboardOverview
  isLoading: boolean
}

export function RevenueProfitPanel({ overview, isLoading }: RevenueProfitPanelProps) {
  // Coverage is the share of net sales whose menu items have a projected
  // cost set — drives the trust badge on Net Profit / Projected Margin.
  const coverage = overview
    ? metricPairOrEmpty(overview.projected_coverage_pct).current
    : 0
  // F-RPT-N41: on the purchases basis the cost is what was bought, so the
  // coverage badge and "% covered" do not describe it. at_sale is unchanged.
  const basis = cogsBasisDisplay(overview?.cogs_basis)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Revenue &amp; profit</CardTitle>
        {!isLoading && overview && basis.showCoverageBadge ? (
          <DataCompletenessBadge
            coverage={coverage}
            detail={
              coverage >= 80
                ? `${coverage.toFixed(0)}% of net sales come from items with a projected cost set — Net Profit and Projected Margin should be reliable.`
                : `Only ${coverage.toFixed(0)}% of net sales have a configured COGS. Items missing a cost are excluded, so Net Profit will under-report COGS until you fill in Menu → Costing tab.`
            }
          />
        ) : null}
      </CardHeader>
      {/* P&L ladder: tax → discounts → net sales → expenses → projected
          margin → net profit. Net Profit and Projected Margin are
          emphasised — the two profit lenses managers care about.
          Refund info lives in the KPI hero row above. */}
      <CardContent className="grid min-w-0 gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">

        {isLoading || !overview ? (
          Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)
        ) : (
          <>
            <ProfitRow metric="sales_tax" label="Sales tax" pair={overview.tax} polarity="positive" />
            <ProfitRow metric="discounts" label="Discounts" pair={overview.discounts} polarity="negative" />
            <ProfitRow
              metric="service_charge"
              label="Service charge"
              pair={metricPairOrEmpty(overview.service_charge)}
              polarity="positive"
            />
            <ProfitRow metric="net_sales" label="Net sales" pair={overview.net_sales} polarity="positive" />
            <ProfitRow metric="operating_expenses" label="Operating expenses" pair={overview.expenses} polarity="negative" />
            <ProfitRow
              metric={basis.marginMetric}
              label={basis.marginLabel}
              pair={metricPairOrEmpty(overview.projected_margin)}
              polarity="positive"
              emphasised
              subtitle={
                <>
                  {metricPairOrEmpty(overview.projected_margin_pct).current.toFixed(1)}% margin
                  {basis.showCoverage && (
                    <>
                      {' '}•{' '}
                      <span
                        className={cn(
                          coverage < 50 && 'text-amber-600 dark:text-amber-400',
                        )}
                      >
                        {coverage.toFixed(0)}% covered
                      </span>
                    </>
                  )}
                </>
              }
            />
            <ProfitRow
              metric="net_profit"
              label="Net profit"
              pair={overview.net_profit}
              polarity="positive"
              emphasised
              subtitle={
                <>
                  After COGS &amp; expenses
                  {basis.showCoverage && (
                    <>
                      {' '}•{' '}
                      <span
                        className={cn(
                          coverage < 50 && 'text-amber-600 dark:text-amber-400',
                        )}
                      >
                        {coverage.toFixed(0)}% covered
                      </span>
                    </>
                  )}
                </>
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ProfitRow({
  metric,
  label,
  pair,
  polarity,
  emphasised,
  subtitle,
}: {
  metric: MetricKey
  label: string
  pair: MetricPair
  polarity: 'positive' | 'negative'
  emphasised?: boolean
  /** Optional small line between the value and the trend pill. Used by the
   *  Projected Margin tile to surface coverage % so users can judge how
   *  trustworthy the assumed-cost number is at a glance. */
  subtitle?: React.ReactNode
}) {
  const { formatCurrency } = useCurrency()
  const dir = trendDirection(pair.pct, polarity)
  const pct = formatTrendPercent(pair.pct)
  const Icon = dir === 'up' ? ArrowUpRight : dir === 'down' ? ArrowDownRight : Minus

  const trendColor =
    dir === 'up'
      ? 'text-emerald-600 dark:text-emerald-400'
      : dir === 'down'
        ? 'text-rose-600 dark:text-rose-400'
        : 'text-muted-foreground'

  return (
    <div className={cn('rounded-md border p-3', emphasised && 'border-primary/30 bg-primary/5')}>
      <MetricLabel
        metric={metric}
        label={label}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      />
      <div className={cn('mt-1 text-lg font-semibold tabular-nums', emphasised && 'text-primary')}>
        {formatCurrency(pair.current)}
      </div>
      {subtitle && <div className="mt-0.5 text-xs text-muted-foreground">{subtitle}</div>}
      <div className={cn('mt-0.5 inline-flex items-center gap-1 text-xs', trendColor)}>
        <Icon className="h-3 w-3" />
        <span>{pct ?? '—'}</span>
        <span className="text-muted-foreground">vs {formatCurrency(pair.previous)}</span>
      </div>
    </div>
  )
}
